import { zodResolver } from "@hookform/resolvers/zod";
import { createJob, getJob, getJobCalcsPreviewView } from "@stryker/core/api/jobs";
import { profileQueryOptions, profileSelectors } from "@stryker/core/queries";
import { QueryKeys } from "@stryker/core/queryKeys";
import type { Job, JobCalcsPreviewResponse, LocalFile } from "@stryker/core/types/jobs";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { useNavigate, useParams } from "react-router-dom";
import { isNullish, uniqueBy } from "remeda";
import { z } from "zod";
import { useShallow } from "zustand/react/shallow";
import { FinalDesignPreviewModal } from "@/components/job/FinalDesignPreviewModal";
import { Button, Typography, useToast } from "@/components/ui";
import { Spinner } from "@/components/ui/spinner";
import { JOB_TYPES } from "@/constants/job";
import { useConfigOptions } from "@/hooks/useConfigOptions";
import { useGetConfig } from "@/hooks/useConfigOptionsAgent";
import useLocationQuery from "@/hooks/useLocationQuery";
import useScreens from "@/hooks/useScreens";
import { cn } from "@/lib/utils";
import useCUMechanicalJobStore from "@/store/useCUMechanicalJobStore";
import { createInitialState } from "@/utils/mappings/mechanical";
import { AiChat } from "../components";
import { selectStateForJob } from "../helpers";
import { ConfirmationDialog } from "./ConfirmationDialog";
import { DocumentUploader } from "./DocumentUploader";
import { JobFormSections } from "./JobFormSections";
import { JobHeader } from "./JobHeader";
import { ModeToggle } from "./ModeToggle";

const jobNameSchema = z.object({
  job_name: z.string().max(30).optional(),
});

type JobNameSchema = z.infer<typeof jobNameSchema>;

type MergedDoc = Array<LocalFile | Job["attached_documents"][number]>;

type CreationMode = "standard" | "ai" | "future";

export default function CreateUpdateMechanicalJob() {
  const [documents, setDocuments] = useState<MergedDoc>([]);
  const [referenceDocuments, setReferenceDocuments] = useState<MergedDoc>([]);
  const [isPositionFixed, setIsPositionFixed] = useState(false);
  const [plantRepairIdentifier, setPlantRepairIdentifier] = useState<string | undefined>(undefined);
  const [showFinalDesignModal, setShowFinalDesignModal] = useState(false);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [finalDesignPreview, setFinalDesignPreview] = useState<JobCalcsPreviewResponse | null>(null);
  const [creationMode, setCreationMode] = useState<CreationMode>("standard");
  const [isInitialHydrationDone, setIsInitialHydrationDone] = useState(false);

  const { data: profile } = useQuery({
    ...profileQueryOptions(),
    select: (response) => ({
      id: response.id,
      canRunCalculations: profileSelectors.canRunCalculations(response),
    }),
  });

  const configHook = useConfigOptions();
  const { data: aiConfigOptions, isLoading: isAiConfigLoading } = useGetConfig();
  const configOptions = {
    repairSystemOptions: configHook.repairSystemOptions ?? [],
    surfacePreparationOptions: configHook.surfacePreparationOptions ?? [],
    temperatureUnitsOptions: configHook.temperatureUnitsOptions ?? [],
    measureUnitOptions: configHook.measureUnitOptions ?? [],
    npsOptions: configHook.npsOptions ?? [],
    materialOptions: configHook.materialOptions ?? [],
    kitOptions: configHook.allKits ?? [],
    jobFlagsOptions: configHook.jobFlagsOptions ?? [],
    usersOptions: configHook.usersOptions ?? [],
    plantOptions: configHook.plantOptions ?? [],
    branchOptions: configHook.branchOptions ?? [],
    repairItemsOptions: configHook.repairItemsOptions ?? [],
    repairUnitTypesOptions: configHook.repairUnitTypesOptions ?? [],
  };

  // Add loading check for config
  const isConfigLoading = configHook.repairSystemOptions === undefined || isAiConfigLoading;

  const userId = String(profile?.id ?? "user-placeholder-123");
  const canRunCalculations = profile?.canRunCalculations;

  const isTabletOrAbove = useScreens("md", "up");
  const { toast } = useToast();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const query = useLocationQuery();
  const jobName = query.get("jobName");
  const { id: draftJobId } = useParams();
  const duplicateJobId = query.get("jobId");
  const jobId = window.location.href.includes("draft-edit") ? draftJobId : duplicateJobId;

  const { data: job, isLoading: isJobLoading } = useQuery({
    queryKey: [QueryKeys.Job, jobId],
    queryFn: () => getJob(jobId as string),
    enabled: !!jobId,
  });

  useEffect(() => {
    setIsInitialHydrationDone(!jobId);
  }, [jobId]);

  useEffect(() => {
    if (!jobId || !job || isInitialHydrationDone) {
      return;
    }
    // we are using draft or copy
    if (job.attached_documents && !documents.some((d) => "link" in d)) {
      setDocuments((prevState) => uniqueBy([...prevState, ...job.attached_documents], (d) => d.name));
    }
    if (job.reference_documents && !referenceDocuments.some((d) => "link" in d)) {
      setReferenceDocuments((prevState) => uniqueBy([...prevState, ...job.reference_documents], (d) => d.name));
    }
    setIsInitialHydrationDone(true);
  }, [documents, isInitialHydrationDone, job, jobId, referenceDocuments]);

  const {
    areAllRequiredFieldsFilled,
    atLeastOneSectionFilled,
    dataForSubmit,
    resetState,
    setDefaultValues,
    setJobIdForDuplication,
    jobIdForDuplication,
    sections,
  } = useCUMechanicalJobStore(useShallow(selectStateForJob));

  // Reset state when jobId changes (switching between jobs)
  useEffect(() => {
    if (jobId && jobIdForDuplication && jobId !== jobIdForDuplication) {
      // Switching from one job to another - reset state first
      resetState();
      setJobIdForDuplication(null);
    }
  }, [jobId, jobIdForDuplication, resetState, setJobIdForDuplication]);

  // For duplication state
  useEffect(() => {
    if (job && jobId && !jobIdForDuplication) {
      // Map job data to mechanical job sections for duplication/draft editing
      const mappedSections = createInitialState(job);
      setDefaultValues(mappedSections);
      setJobIdForDuplication(jobId);
    }
  }, [job, jobId, jobIdForDuplication, setDefaultValues, setJobIdForDuplication]);

  // Reset state on component unmount (leaving the flow entirely)
  useEffect(() => {
    return () => {
      resetState();
    };
  }, [resetState]);

  useEffect(() => {
    const bodyElement = document.body;
    const topPos = bodyElement.getBoundingClientRect().top;

    if (topPos < -100 && !isPositionFixed) {
      setIsPositionFixed(true);
    } else if (topPos >= -100 && isPositionFixed) {
      setIsPositionFixed(false);
    }
  }, []);

  useEffect(() => {
    const handleScroll = () => {
      const bodyElement = document.body;
      const topPos = bodyElement.getBoundingClientRect().top;

      if (topPos < -100 && !isPositionFixed) {
        setIsPositionFixed(true);
      } else if (topPos >= -100 && isPositionFixed) {
        setIsPositionFixed(false);
      }
    };
    window.addEventListener("scroll", handleScroll, false);

    return () => {
      window.removeEventListener("scroll", handleScroll, false);
    };
  }, [isPositionFixed]);

  const {
    register,
    formState: { errors },
    handleSubmit,
  } = useForm<JobNameSchema>({
    resolver: zodResolver(jobNameSchema),
    values: {
      job_name: jobName ? decodeURIComponent(jobName) : "",
    },
  });

  const { mutate: mutateCreateJob, isPending: isPendingCreateJob } = useMutation({
    mutationFn: createJob,
    onError: (error: string) => {
      toast({ variant: "destructive", description: error?.toString() });
    },
    onSuccess: async (response) => {
      resetState();
      await Promise.all([
        void queryClient.invalidateQueries({ queryKey: [QueryKeys.JobsConfig] }),
        void queryClient.invalidateQueries({ queryKey: [QueryKeys.JobsList] }),
        void queryClient.invalidateQueries({ queryKey: [QueryKeys.Job, response.id] }),
      ]);
      navigate(`/jobs/${response.id}`);
    },
    onSettled: () => {
      setShowConfirmModal(false);
    },
  });

  const { mutate: mutateGetFinalDesignPreview, isPending: isLoadingFinalDesignPreview } = useMutation({
    mutationFn: getJobCalcsPreviewView,
    onError: (error: string) => {
      const message = error?.toString() || "Unable to generate preview.";
      toast({ variant: "destructive", description: message });
      setFinalDesignPreview({ previews: [], error: message });
    },
    onSuccess: (response) => {
      setFinalDesignPreview(response);
    },
  });

  function handleModeSwitch(mode: CreationMode) {
    if (mode === "ai" && isConfigLoading) {
      toast({ variant: "destructive", description: "Config loading, please wait." });
      return;
    }
    setCreationMode(mode);
  }

  const isToggleDisabled = isConfigLoading || isPendingCreateJob;

  function getJobData(is_draft: boolean) {
    const payload: Record<string, any> = {
      job_name: plantRepairIdentifier,
      is_draft,
      job_type: JOB_TYPES.mechanical,
      job_product_type: "mechanical",
      ...dataForSubmit,
      repair_system_max_temp: configHook.repairSystems?.find((r) => r.id == dataForSubmit?.repair_system)
        ?.repair_system_max_temp,
    };

    if (Array.isArray(payload.new_notes)) {
      payload.new_notes = (payload.new_notes as Array<{ custom_name_of_note?: string; custom_note?: string }>).filter(
        (note) => note?.custom_name_of_note || note?.custom_note
      );
    }

    for (const key in payload) {
      if (
        payload[key] === "" ||
        (Array.isArray(payload[key]) && payload[key].length === 0) ||
        isNullish(payload[key])
      ) {
        delete payload[key];
      }
      // check if we send empty notes
      if (
        key === "new_notes" &&
        payload[key]?.length === 1 &&
        payload[key][0].custom_name_of_note === "" &&
        payload[key][0].custom_note === ""
      ) {
        delete payload[key];
      }

      // check if we are sending empty components data
      if (
        key === "components_data" &&
        payload[key]?.length === 1 &&
        Object.values(payload[key][0]).every((v: any) => !v)
      ) {
        delete payload[key];
      }
    }

    const formData = new FormData();
    if (documents.length || referenceDocuments.length) {
      for (const property in payload) {
        const value = payload[property];
        if (Array.isArray(value) && value.filter(Boolean).length) {
          value.forEach((v: any) => {
            formData.append(property, JSON.stringify(v));
          });
        } else if (!Array.isArray(value)) {
          if (value !== null && typeof value === "object") {
            formData.append(property, JSON.stringify(value));
          } else {
            if (value !== undefined) {
              formData.append(property, typeof value !== "string" ? String(value) : value);
            }
          }
        }
      }

      documents.forEach((doc) => {
        if ("file" in doc) {
          formData.append("attached_documents", doc.file);
        } else {
          formData.append("attached_documents", doc.name);
        }
      });

      referenceDocuments.forEach((doc) => {
        if ("file" in doc) {
          formData.append("reference_documents", doc.file);
        } else {
          formData.append("reference_documents", doc.name);
        }
      });
    }
    return documents.length || referenceDocuments.length ? formData : payload;
  }

  const isMultipart = !!documents.length || !!referenceDocuments.length;

  function handleCreateJob(is_draft = false) {
    const data = getJobData(is_draft);
    mutateCreateJob({ data, isMultipart });
  }

  function handleSaveAsDraft() {
    handleSubmit((data) => {
      setPlantRepairIdentifier(data.job_name);
      handleCreateJob(true);
    })();
  }

  function handleRunCalculations() {
    handleSubmit((data) => {
      setPlantRepairIdentifier(data.job_name);
      setFinalDesignPreview(null);
      setShowConfirmModal(false);
      setShowFinalDesignModal(true);
      const previewPayload = getFinalDesignPreviewPayload();
      mutateGetFinalDesignPreview(previewPayload);
    })();
  }

  function handleDocumentAdd(file: LocalFile) {
    setDocuments((prevState) => [...prevState, file]);
  }

  function handleDocumentRemove(name: string) {
    setDocuments((prevState) => prevState.filter((p) => p.name !== name));
  }

  function handleReferenceDocumentAdd(file: LocalFile) {
    setReferenceDocuments((prevState) => [...prevState, file]);
  }

  function handleReferenceDocumentRemove(name: string) {
    setReferenceDocuments((prevState) => prevState.filter((p) => p.name !== name));
  }

  function getFinalDesignPreviewPayload() {
    const payload: Record<string, any> = {
      job_type: JOB_TYPES.mechanical,
      job_product_type: "mechanical",
      ...dataForSubmit,
    };

    for (const key in payload) {
      if (payload[key] === "" || (Array.isArray(payload[key]) && payload[key].length === 0) || payload[key] == null) {
        delete payload[key];
      }
    }

    return {
      data: payload,
      isMultipart: false,
    };
  }

  function handleFinalDesignContinue() {
    setShowFinalDesignModal(false);
    setTimeout(() => {
      setShowConfirmModal(true);
    }, 100);
  }

  // Check if Joint Efficiency section should be shown
  const shouldShowJointEfficiency =
    sections.checks.isDataFilled && sections.checks.data.do_you_wish_to_change_default_joint_efficiency === true;

  const shouldBlockUntilHydrated = !!jobId && (isJobLoading || !isInitialHydrationDone);

  if (shouldBlockUntilHydrated) {
    return (
      <div className="flex min-h-[400px] items-center justify-center">
        <Spinner svgClassName="h-20 w-20" />
      </div>
    );
  }

  return (
    <div className="relative pb-6">
      <JobHeader
        isPositionFixed={isPositionFixed}
        register={register}
        errors={errors}
        atLeastOneSectionFilled={atLeastOneSectionFilled}
        areAllRequiredFieldsFilled={areAllRequiredFieldsFilled}
        isPendingCreateJob={isPendingCreateJob}
        isPendingPreview={isLoadingFinalDesignPreview}
        onSaveAsDraft={handleSaveAsDraft}
        onRunCalculations={handleRunCalculations}
        canRunCalculations={canRunCalculations}
      />

      <ModeToggle creationMode={creationMode} isToggleDisabled={isToggleDisabled} onModeChange={handleModeSwitch} />

      <div className={cn(creationMode === "ai" && "pt-[140px]")}>
        {creationMode === "ai" && (
          <AiChat
            isFullPage={true}
            aiConfigOptions={aiConfigOptions}
            isConfigLoading={isConfigLoading}
            userId={userId}
            setDefaultValues={(values: Record<string, unknown>) => setDefaultValues(values as any)}
            toast={toast}
            navigate={navigate}
            sections={sections}
            onClose={() => setCreationMode("standard")}
          />
        )}
        {creationMode === "standard" && (
          <>
            <JobFormSections
              temperatureUnitsOptions={configOptions.temperatureUnitsOptions}
              shouldShowJointEfficiency={shouldShowJointEfficiency}
            />
            <DocumentUploader
              documents={documents}
              referenceDocuments={referenceDocuments}
              onDocumentAdd={handleDocumentAdd}
              onDocumentRemove={handleDocumentRemove}
              onReferenceDocumentAdd={handleReferenceDocumentAdd}
              onReferenceDocumentRemove={handleReferenceDocumentRemove}
            />

            {!isTabletOrAbove && (
              <div className="mb-4">
                {!duplicateJobId && (
                  <Button
                    disabled={!atLeastOneSectionFilled || !!errors.job_name?.message || isPendingCreateJob}
                    onClick={handleSaveAsDraft}
                    className="mb-2 w-full"
                    size="lg"
                  >
                    Save as draft
                  </Button>
                )}
                {canRunCalculations && (
                  <Button
                    disabled={
                      !areAllRequiredFieldsFilled ||
                      !!errors.job_name?.message ||
                      isPendingCreateJob ||
                      isLoadingFinalDesignPreview
                    }
                    onClick={handleRunCalculations}
                    className="w-full"
                    size="lg"
                  >
                    Run calculations
                  </Button>
                )}
              </div>
            )}
          </>
        )}
        {creationMode === "future" && (
          <div className="mt-9 p-6 text-center">
            <Typography variant="text-lg" weight="semibold">
              Future Option
            </Typography>
            <p className="mt-4 text-gray-600">
              This option will be available soon. Switch back to Standard or AI Agent for now.
            </p>
          </div>
        )}
      </div>
      <ConfirmationDialog
        open={showConfirmModal}
        isPending={isPendingCreateJob}
        onClose={() => setShowConfirmModal(false)}
        onConfirm={() => handleCreateJob(false)}
      />
      <FinalDesignPreviewModal
        open={showFinalDesignModal}
        preview={finalDesignPreview}
        isLoading={isLoadingFinalDesignPreview}
        onClose={() => {
          setShowFinalDesignModal(false);
          setFinalDesignPreview(null);
        }}
        onContinue={handleFinalDesignContinue}
      />
    </div>
  );
}
